import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { Claims } from "../api/claims";

const PASSWORD_MAX_AGE_DAYS = 365;
/**
 * Grace period (ms) after clearExpired() during which checkClaimsExpiry()
 * always returns false. This prevents the route guard from immediately
 * re-flagging the user as expired before Firebase custom claims propagate
 * the new passwordUpdatedAt value into the ID token.
 */
const CLEAR_GRACE_PERIOD_MS = 30_000;

@Injectable({
  providedIn: "root",
})
export class PasswordExpiryService {
  private readonly passwordExpiredSubject = new BehaviorSubject<boolean>(false);
  private lastClearedAt: number = 0;

  passwordExpired$ = this.passwordExpiredSubject.asObservable();

  setExpired(): void {
    this.passwordExpiredSubject.next(true);
  }

  clearExpired(): void {
    this.lastClearedAt = Date.now();
    this.passwordExpiredSubject.next(false);
  }

  /**
   * Returns true if claims indicate password is expired (passwordUpdatedAt > 90 days ago).
   * Missing passwordUpdatedAt is treated as not expired (backward compatibility).
   * Returns false during a grace period after clearExpired() to allow Firebase
   * custom claims propagation.
   */
  checkClaimsExpiry(claims: Claims | null): boolean {
    if (
      this.lastClearedAt &&
      Date.now() - this.lastClearedAt < CLEAR_GRACE_PERIOD_MS
    ) {
      return false;
    }
    if (!claims?.passwordUpdatedAt) return false;
    const expiryMs = PASSWORD_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() - claims.passwordUpdatedAt > expiryMs;
  }

  get isExpired(): boolean {
    return this.passwordExpiredSubject.value;
  }
}
